/**
 * The blackjack table: deal, decide, settle. Deterministic from a seed, I/O-free.
 *
 * ============================ SHAPE OF THE THING ================================
 * bet -> deal -> (insurance, if the dealer shows an ace) -> each seat acts on each of
 * its hands in order -> dealer plays out -> settle.
 *
 * A split is modelled as a SECOND HAND on the same seat rather than as a special case
 * inside one hand. Everything downstream — acting, settling, the analysis — then works
 * on "a hand" and never has to ask whether it came from a split. The alternative,
 * carrying two totals inside one object, is where blackjack implementations
 * traditionally go wrong: doubling after a split, settling one half against a dealer
 * natural, and busting one hand while the other stands are all ordinary in this shape
 * and all special cases in the other.
 *
 * ## The dealer peeks, and it matters
 *
 * With an ace or a ten showing the dealer checks for a natural BEFORE anyone acts.
 * That is not decoration: it ends the hand immediately when it hits, and when it
 * misses it is information — it is why `analysis.ts` conditions on the peek, and it is
 * worth about 1.4% of a bet on the closest decisions. The peek is resolved here, once,
 * and the result is carried on the table so nothing recomputes it differently.
 *
 * ## Money
 *
 * Blackjack is played against the house, so chips are NOT conserved among players the
 * way they are in the poker engine. What is invariant is that every hand settles to
 * exactly one of the legal payouts and the seat's net is the sum of them. That is what
 * the tests assert.
 * ================================================================================
 */
import { seedBytes } from "../deck";
import { type CryptoSeed } from "../rng";
import { blackjackShoe } from "./shoe";
import type { Card } from "../types";
import {
  ACE_RANK, COMP_SLOTS, freshComposition, rankSlot, rankValue,
  type BlackjackRules,
} from "./rules";

export type Action = "hit" | "stand" | "double" | "split";
export type Phase = "dealing" | "insurance" | "acting" | "dealer" | "settled";

export interface PlayerHand {
  cards: Card[];
  bet: number;
  doubled: boolean;
  /** Came out of a split — used for the one-card-to-split-aces rule and for DAS. */
  fromSplit: boolean;
  splitAces: boolean;
  done: boolean;
  /** Filled at settlement: what this hand returned, net of its stake. */
  settled: number | null;
  outcome: "win" | "lose" | "push" | "blackjack" | null;
}

export interface Seat {
  id: string;
  isHero: boolean;
  hands: PlayerHand[];
  /** Half the base bet when taken, 0 otherwise. */
  insurance: number;
  insuranceSettled: number;
}

const total = (cards: readonly Card[]): { total: number; soft: boolean } => {
  let t = 0, aces = 0;
  for (const c of cards) {
    const v = rankValue(c >> 2);
    if (v === 11) aces++;
    t += v === 11 ? 1 : v;
  }
  const soft = aces > 0 && t + 10 <= 21;
  return { total: soft ? t + 10 : t, soft };
};

export const handTotal = total;
export const isNatural = (h: PlayerHand): boolean =>
  !h.fromSplit && h.cards.length === 2 && total(h.cards).total === 21;

export class BlackjackTable {
  readonly rules: BlackjackRules;
  readonly seats: Seat[];
  dealer: Card[] = [];
  phase: Phase = "dealing";
  /** Resolved at the peek, before anyone acts. Null until then. */
  dealerNatural: boolean | null = null;
  /** Seat index and hand index currently to act. */
  turn: { seat: number; hand: number } | null = null;

  private shoe: Card[] = [];
  private drawn = 0;
  private readonly seed: Uint8Array;
  private handNo = 0;

  constructor(opts: {
    rules: BlackjackRules;
    seats: { id: string; isHero: boolean }[];
    seed?: CryptoSeed;
  }) {
    this.rules = opts.rules;
    this.seed = opts.seed ? seedBytes(opts.seed) : seedBytes(new Uint8Array(32));
    this.seats = opts.seats.map((s) => ({
      id: s.id, isHero: s.isHero, hands: [], insurance: 0, insuranceSettled: 0,
    }));
  }

  /**
   * A fresh shoe for this hand.
   *
   * The arithmetic lives in `shoe.ts` because `/fair` runs it too — one implementation
   * dealing and verifying, so the verification page cannot drift from the table it
   * claims to be checking. See that file for the domain-separation reasoning.
   */
  private newShoe(handNo: number): void {
    this.shoe = blackjackShoe(this.seed, handNo, this.rules);
    this.drawn = 0;
  }

  private draw(): Card {
    if (this.drawn >= this.shoe.length) throw new Error("shoe exhausted");
    return this.shoe[this.drawn++]!;
  }

  /** Cards every player can see right now — what the analysis removes from the deck. */
  visible(): Card[] {
    const out: Card[] = [];
    for (const s of this.seats) for (const h of s.hands) out.push(...h.cards);
    // The hole card is face down and stays out of this, even after the peek.
    if (this.dealer.length) out.push(this.dealer[0]!);
    if (this.phase === "dealer" || this.phase === "settled") out.push(...this.dealer.slice(1));
    return out;
  }

  /** The remaining composition as the analysis wants it: 10 slots, hole card unknown. */
  composition(): Int32Array {
    const c = freshComposition(this.rules);
    for (const card of this.visible()) c[rankSlot(card >> 2)]!--;
    for (let i = 0; i < COMP_SLOTS; i++) if (c[i]! < 0) c[i] = 0;
    return c;
  }

  get dealerUp(): Card | null { return this.dealer[0] ?? null; }

  /**
   * Which hand of this sitting is on the table, counting from one.
   *
   * Exposed so the room can tell a player which hand number to check on `/fair`. The
   * label the shoe is derived under is this minus one — see `shoe.ts`, where that
   * off-by-one is deliberate and explained.
   */
  get handNumber(): number { return this.handNo; }

  startHand(bet: number): void {
    if (!(bet > 0)) throw new Error("a bet must be positive");
    this.handNo++;
    this.newShoe(this.handNo - 1);   // the label is zero-based; see shoe.ts
    this.dealer = [];
    this.dealerNatural = null;
    for (const s of this.seats) {
      s.insurance = 0; s.insuranceSettled = 0;
      s.hands = [{
        cards: [], bet, doubled: false, fromSplit: false, splitAces: false,
        done: false, settled: null, outcome: null,
      }];
    }
    // Standard order: one card to each seat, one to the dealer, then a second round.
    for (const s of this.seats) s.hands[0]!.cards.push(this.draw());
    this.dealer.push(this.draw());
    for (const s of this.seats) s.hands[0]!.cards.push(this.draw());
    this.dealer.push(this.draw());

    const upRank = this.dealer[0]! >> 2;
    if (this.rules.offerInsurance && upRank === ACE_RANK) {
      this.phase = "insurance";
      return;
    }
    this.resolvePeek();
  }

  /** Take or decline insurance for a seat. Half the base bet. */
  takeInsurance(seatIndex: number, take: boolean): void {
    if (this.phase !== "insurance") throw new Error("insurance is not open");
    const s = this.seats[seatIndex];
    if (!s) throw new Error("no such seat");
    s.insurance = take ? s.hands[0]!.bet / 2 : 0;
  }

  closeInsurance(): void {
    if (this.phase !== "insurance") throw new Error("insurance is not open");
    this.resolvePeek();
  }

  /**
   * The peek. Ends the hand immediately on a dealer natural.
   *
   * Insurance settles here and only here: it is a bet on the hole card, so it is
   * decided the moment the hole card is known and never touches the main wager.
   */
  private resolvePeek(): void {
    const up = this.dealer[0]! >> 2;
    const peeks = up === ACE_RANK || rankValue(up) === 10;
    const natural = total(this.dealer).total === 21;
    this.dealerNatural = peeks ? natural : false;

    for (const s of this.seats) {
      if (s.insurance > 0) {
        s.insuranceSettled = natural ? s.insurance * this.rules.insurancePays : -s.insurance;
      }
    }

    if (peeks && natural) { this.settle(); return; }
    this.phase = "acting";
    this.turn = this.nextTurn(0, 0);
    if (!this.turn) this.playDealer();
  }

  private nextTurn(seat: number, hand: number): { seat: number; hand: number } | null {
    for (let si = seat; si < this.seats.length; si++) {
      const s = this.seats[si]!;
      for (let hi = si === seat ? hand : 0; hi < s.hands.length; hi++) {
        const h = s.hands[hi]!;
        if (h.done) continue;
        // A natural needs no decision, and neither does a 21 after a split.
        if (total(h.cards).total >= 21) { h.done = true; continue; }
        return { seat: si, hand: hi };
      }
    }
    return null;
  }

  /** Actions legal for the hand to act, in a fixed order for the UI and the analysis. */
  legalActions(): Action[] {
    if (this.phase !== "acting" || !this.turn) return [];
    const s = this.seats[this.turn.seat]!;
    const h = s.hands[this.turn.hand]!;
    const first = h.cards.length === 2;
    const out: Action[] = ["hit", "stand"];
    if (h.splitAces) return [];                        // one card only, no decisions
    if (first && (this.rules.doubleAnyTwo || [9, 10, 11].includes(total(h.cards).total))
        && (!h.fromSplit || this.rules.doubleAfterSplit)) out.push("double");
    if (first && s.hands.length < this.rules.maxHands
        && rankSlot(h.cards[0]! >> 2) === rankSlot(h.cards[1]! >> 2)) out.push("split");
    return out;
  }

  act(action: Action): void {
    if (this.phase !== "acting" || !this.turn) throw new Error("nobody is to act");
    if (!this.legalActions().includes(action)) throw new Error(`illegal action: ${action}`);
    const s = this.seats[this.turn.seat]!;
    const h = s.hands[this.turn.hand]!;

    switch (action) {
      case "stand": h.done = true; break;
      case "hit": {
        h.cards.push(this.draw());
        if (total(h.cards).total >= 21) h.done = true;
        break;
      }
      case "double": {
        h.bet *= 2; h.doubled = true;
        h.cards.push(this.draw());
        h.done = true;
        break;
      }
      case "split": {
        const moved = h.cards.pop()!;
        const aces = rankSlot(h.cards[0]! >> 2) === rankSlot(ACE_RANK);
        const second: PlayerHand = {
          cards: [moved], bet: h.bet, doubled: false, fromSplit: true,
          splitAces: aces && this.rules.oneCardToSplitAces,
          done: false, settled: null, outcome: null,
        };
        h.fromSplit = true;
        h.splitAces = aces && this.rules.oneCardToSplitAces;
        s.hands.splice(this.turn.hand + 1, 0, second);
        h.cards.push(this.draw());
        second.cards.push(this.draw());
        if (h.splitAces) { h.done = true; second.done = true; }
        break;
      }
    }
    this.turn = this.nextTurn(this.turn.seat, this.turn.hand);
    if (!this.turn) this.playDealer();
  }

  private playDealer(): void {
    this.phase = "dealer";
    const anyLive = this.seats.some((s) => s.hands.some((h) => total(h.cards).total <= 21));
    if (anyLive) {
      for (;;) {
        const { total: t, soft } = total(this.dealer);
        const stand = this.rules.dealerStandsSoft17 ? t >= 17 : t > 17 || (t === 17 && !soft);
        if (stand || t > 21) break;
        this.dealer.push(this.draw());
      }
    }
    this.settle();
  }

  /**
   * Settlement, one hand at a time.
   *
   * A natural beats a plain 21 and pushes another natural; a split hand can never BE a
   * natural, which is why `isNatural` checks `fromSplit`. Those two rules are worth
   * more than they look: getting the first wrong overpays every 21, and getting the
   * second wrong pays 3:2 on split tens.
   */
  private settle(): void {
    const dTotal = total(this.dealer).total;
    const dNat = this.dealerNatural === true;
    for (const s of this.seats) {
      for (const h of s.hands) {
        const t = total(h.cards).total;
        const nat = isNatural(h);
        if (nat && dNat) { h.outcome = "push"; h.settled = 0; }
        else if (nat) { h.outcome = "blackjack"; h.settled = h.bet * this.rules.blackjackPays; }
        else if (dNat) { h.outcome = "lose"; h.settled = -h.bet; }
        else if (t > 21) { h.outcome = "lose"; h.settled = -h.bet; }
        else if (dTotal > 21 || t > dTotal) { h.outcome = "win"; h.settled = h.bet; }
        else if (t < dTotal) { h.outcome = "lose"; h.settled = -h.bet; }
        else { h.outcome = "push"; h.settled = 0; }
        h.done = true;
      }
    }
    this.turn = null;
    this.phase = "settled";
  }

  /** A seat's net for the hand, main wagers plus the insurance side bet. */
  net(seatIndex: number): number {
    const s = this.seats[seatIndex]!;
    return s.hands.reduce((a, h) => a + (h.settled ?? 0), 0) + s.insuranceSettled;
  }
}
